import { Dispatch, SetStateAction, useState } from "react";
import { ActionIcon, Alert, Button, Checkbox, Group, Loader, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import customerService from "@/services/customer";
import { Customer } from "@/models/response/customer";
import { normalizeDigits } from "@/modules/marketing/utils";
import classes from "@/modules/marketing/campaign.module.css";

const PAGE_SIZE = 20;
type Props = {
  selectedItems: Customer[];
  setSelectedItems: Dispatch<SetStateAction<Customer[]>>;
};

export default function SelectUsers({ selectedItems, setSelectedItems }: Props) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState("");
  const normalized = normalizeDigits(search).trim();
  const [term] = useDebouncedValue(normalized, 350);
  const canSearch = normalized.length >= 2;
  const waiting = canSearch && normalized !== term;
  const ready = canSearch && !waiting;
  const query = useQuery({
    queryKey: ["campaign-customer-search", term, page],
    queryFn: () => customerService.getCustomers({ Term: term, Skip: page * PAGE_SIZE, Count: PAGE_SIZE }),
    enabled: ready,
    staleTime: 30_000,
    retry: false,
  });
  const results = query.data?.result ?? [];
  const matchingSelected = selectedItems.filter(item => normalizeDigits([item.firstName, item.lastName, item.phoneNumber].join(" ")).includes(normalizeDigits(selectedSearch).trim()));
  const visibleSelected = expanded ? matchingSelected.slice(0, PAGE_SIZE) : selectedItems.slice(0, 5);
  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const toggle = (item: Customer, checked: boolean) => setSelectedItems(previous =>
    checked
      ? previous.some(customer => customer.id === item.id) ? previous : [...previous, item]
      : previous.filter(customer => customer.id !== item.id)
  );

  return <Stack gap="md">
    <TextInput
      size="md"
      label="جستجوی مشتری"
      description="حداقل ۲ حرف از نام یا ۲ رقم از شماره موبایل را بنویسید."
      placeholder="مثلاً سارا یا ۰۹۱۲"
      value={search}
      onChange={event => changeSearch(event.currentTarget.value)}
      leftSection={<IconSearch size={18} />}
      rightSection={search ? <ActionIcon variant="subtle" aria-label="پاک کردن جستجو" onClick={() => changeSearch("")}><IconX size={15} /></ActionIcon> : undefined}
    />
    {selectedItems.length > 0 && <div className={classes.selectedCustomers}>
      <Group justify="space-between" mb="sm">
        <Text size="sm">{selectedItems.length.toLocaleString("fa-IR")} مشتری انتخاب شده</Text>
        <Button variant="subtle" color="gray" size="compact-sm" onClick={() => setSelectedItems([])}>پاک کردن انتخاب‌ها</Button>
      </Group>
      {expanded && <TextInput mb="sm" size="sm" label="جستجو در انتخاب‌های شما" value={selectedSearch} onChange={event => setSelectedSearch(event.currentTarget.value)} placeholder="نام یا شمارهٔ مشتری انتخاب‌شده" />}
      <Group gap="xs" className={classes.selectedChips}>
        {visibleSelected.map(item => <Button key={item.id} variant="light" color="violet" size="compact-sm" rightSection={<IconX size={12} />} aria-label={"حذف " + ([item.firstName, item.lastName].filter(Boolean).join(" ") || item.phoneNumber)} onClick={() => toggle(item, false)}>
          {[item.firstName, item.lastName].filter(Boolean).join(" ") || item.phoneNumber}
        </Button>)}
      </Group>
      {expanded && matchingSelected.length > PAGE_SIZE && <Text size="xs" c="dimmed" mt="xs">۲۰ انتخاب اول نمایش داده می‌شود؛ برای پیدا کردن بقیه، در انتخاب‌ها جستجو کنید.</Text>}
      {expanded && matchingSelected.length === 0 && <Text size="sm" c="dimmed">انتخابی با این عبارت پیدا نشد.</Text>}
      {selectedItems.length > 5 && <Button mt="sm" size="compact-sm" variant="subtle" onClick={() => setExpanded(!expanded)}>
        {expanded ? "نمایش کمتر" : "بررسی انتخاب‌ها (" + selectedItems.length.toLocaleString("fa-IR") + ")"}
      </Button>}
    </div>}
    {!canSearch && <div className={classes.empty}><IconSearch size={26} /><div>مشتری موردنظرتان را جستجو کنید.</div><Text size="xs" c="dimmed">برای انتخاب چند نفر، جستجو را تکرار کنید؛ انتخاب‌های قبلی حفظ می‌شوند.</Text></div>}
    {waiting && <Group gap="xs" role="status"><Loader size="xs" /><Text size="sm" c="dimmed">در انتظار تکمیل جستجو…</Text></Group>}
    {ready && <div aria-live="polite">
      {query.isLoading ? <Stack gap="xs" role="status" aria-label="در حال جستجوی مشتری"><Skeleton height={52} /><Skeleton height={52} /></Stack>
        : query.isError ? <Alert color="red">جستجو انجام نشد.<Button variant="subtle" onClick={() => void query.refetch()}>تلاش دوباره</Button></Alert>
        : results.length ? <Stack gap="sm">
          <Text size="xs" c="dimmed">نتایج جستجو · صفحهٔ {(page + 1).toLocaleString("fa-IR")}</Text>
          <div className={classes.customerResults}>{results.map(item => <Checkbox
            key={item.id}
            p="sm"
            styles={{ root: { border: "1px solid #ebe5f0", borderRadius: 10 }, body: { alignItems: "center" }, labelWrapper: { flex: 1 }, label: { width: "100%" } }}
            checked={selectedItems.some(selected => selected.id === item.id)}
            onChange={event => toggle(item, event.currentTarget.checked)}
            label={<Group justify="space-between" gap="xs"><span>{[item.firstName, item.lastName].filter(Boolean).join(" ") || "مشتری بدون نام"}</span><Text size="xs" c="dimmed" dir="ltr">{item.phoneNumber}</Text></Group>}
          />)}</div>
        </Stack> : <div className={classes.empty}>{page > 0 ? "نتیجهٔ دیگری وجود ندارد؛ به صفحهٔ قبل برگردید." : "مشتری‌ای پیدا نشد. بخش دیگری از نام یا شماره را امتحان کنید."}</div>}
      {!query.isLoading && !query.isError && (page > 0 || results.length === PAGE_SIZE) && <Group justify="space-between" mt="md">
        <Button variant="default" disabled={page === 0 || query.isFetching} onClick={() => setPage(value => value - 1)}>صفحهٔ قبل</Button>
        <Button variant="light" disabled={results.length < PAGE_SIZE || query.isFetching} onClick={() => setPage(value => value + 1)}>نتایج بعدی</Button>
      </Group>}
    </div>}
  </Stack>;
}
